import React from 'react';
import { withRouter } from 'react-router-dom';
import {
  get, set, find, includes, forEach, isEqual, without,
} from 'lodash';
import isEmpty from 'lodash/isEmpty';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  TextField,
  Typography,
  Select,
  ListItemText,
  MenuItem,
  InputLabel,
  IconButton,
  FormControl,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import Delete from '@mui/icons-material/Delete';
import { Image } from 'cloudinary-react';
import { connect } from 'react-redux';
import Geocode from 'react-geocode';

import {
  uploadLocationImage,
  CLOUD_NAME,
  LOCATION_BUCKET_NAME,
  convertImageUrlToPublicId,
} from '../../lib/cloudinary';

import SmallMap from '../common/small-map';
import ErrorMessage from '../common/error-message';
import MultiSelect from '../common/multi-select';
import {
  stateAbbreviations,
  LOC_OPEN as ACTIVE,
  LOC_INACTIVE as INACTIVE,
  OpsBrandNames,
  LocationTypeNames,
  locationTypeNames,
  opsBrandNameValues,
  getTargetVehicleCounts,
  OpsBrandNamesLocationTargets,
} from '../../lib/utils';

Geocode.setApiKey('AIzaSyD5CTr-MsQsivSROy8LuLvbvgrZWLNIoKk');
Geocode.enableDebug();

const styles = {
  group: {
    width: 'auto',
    height: 'auto',
    display: 'flex',
    flexDirection: 'row',
  },
  checkbox: {
    marginBottom: '12px',
    fontSize: '18px',
  },
};

function isLink(location) {
  return get(location, 'brands', []).includes(OpsBrandNames.LINK);
}

function addressPart(addressComponents, addrType) {
  const comp = addressComponents.find((cmp) => cmp.types.find((c) => c === addrType));
  if (comp) {
    return comp.short_name;
  }
}

class LocationEditForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      location: props.editLocation,
      error: false,
      unSavedChanges: false,
    };

    this.handleSave = this.handleSave.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleButtonEventChange = this.handleButtonEventChange.bind(this);
    this.handleBrandChange = this.handleBrandChange.bind(this);
    this.handleTargetVehicleChange = this.handleTargetVehicleChange.bind(this);
    this.handleCenterChangeViaMap = this.handleCenterChangeViaMap.bind(this);
    this.handleCheckBoxChange = this.handleCheckBoxChange.bind(this);
    this.disabled = this.disabled.bind(this);
    this.selectedType = this.selectedType.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.uploadImage = this.uploadImage.bind(this);
    this.deleteImage = this.deleteImage.bind(this);
  }

  get brandTypes() {
    const { location } = this.state;
    const brands = get(location, 'brands');
    const MARKET_CONSTANTS = [
      { brand: OpsBrandNames.LINK, disabled: false },
    ];
    let marketsToDisplay = [];
    if (brands) {
      forEach(brands, (brand) => {
        marketsToDisplay.push(find(MARKET_CONSTANTS, (market) => market.brand === brand));
      });
    } else {
      marketsToDisplay = MARKET_CONSTANTS;
    }

    return marketsToDisplay;
  }

  get coordinates() {
    const { location } = this.state;
    const coords = get(location, 'geo.coordinates');
    if (coords && coords.length >= 2) {
      return { lat: coords[1], lng: coords[0] };
    } else {
      return { lat: 42.3523, lng: -71.0594 };
    }
  }

  closeModal() {
    const { onCancel, editLocation } = this.props;
    const { location } = this.state;
    if (!isEqual(editLocation, location)) {
      this.setState({ unSavedChanges: true });
    } else {
      onCancel();
    }
  }

  uploadImage(e) {
    const { location } = this.state;
    const prevImages = get(location, 'images', []);
    uploadLocationImage(e)
      .then((data) => {
        const newImages = data.map((i) => ({ url: i.secure_url }));
        const newLocation = {
          ...location,
          images: prevImages.concat(newImages),
        };
        this.setState({ location: newLocation });
      });
  }

  deleteImage(url) {
    const { location } = this.state;
    const prevImages = get(location, 'images', []);
    const newImages = without(
      prevImages,
      find(prevImages, (locImage) => locImage.url === url),
    );
    const newLocation = {
      ...location,
      images: newImages,
    };
    this.setState({ location: newLocation });
  }

  handleCenterChangeViaMap(lat, lng) {
    Geocode.fromLatLng(lat, lng).then(
      (response) => {
        const addrComponents = response.results[0].address_components;
        const streetNumber = addressPart(addrComponents, 'street_number');
        const route = addressPart(addrComponents, 'route');
        const city = addressPart(addrComponents, 'locality');
        const state = addressPart(addrComponents, 'administrative_area_level_1');
        const zip = addressPart(addrComponents, 'postal_code');
        // const country = addressPart(addrComponents, 'country');
        const { location } = this.state;
        const coords = [lng, lat];
        const address = (streetNumber && route ? `${streetNumber} ${route}` : undefined);

        const newLocation = {
          ...location,
          address,
          city,
          state,
          zip,
          geo: {
            coordinates: coords,
            type: 'Point',
          },
        };
        this.setState({ location: newLocation });
      },
      (error) => {
        console.error(error);
      },
    );
  }

  handleChange(event) {
    const { location } = this.state;

    const newLocation = {
      ...location,
      [event.target.name]: event.target.value,
    };

    this.setState({ location: newLocation });
  }

  handleButtonEventChange(event) {
    const { location } = this.state;

    const newLocation = {
      ...location,
      [event.currentTarget.name]: event.currentTarget.value,
    };

    this.setState({ location: newLocation });
  }

  handleBrandChange(newBrands = []) {
    const { location } = this.state;
    const newLocation = {
      ...location,
      brands: newBrands,
    };

    // unset target vehicle counts for all non-selected brands
    const allBrands = Object.values(OpsBrandNames);
    allBrands.forEach((brand) => {
      const brandKey = OpsBrandNamesLocationTargets[brand.toUpperCase()];
      if (!newBrands.includes(brand) && brandKey) {
        set(newLocation, `targetVehicleCounts.${brandKey}`, 0);
      }
    });
    this.setState({ location: newLocation });
  }

  handleTargetVehicleChange(event) {
    const { name, value } = event.target;
    const { location } = this.state;
    const newLocation = {
      ...location,
    };
    set(newLocation, `targetVehicleCounts.${name}`, value);
    this.setState({ location: newLocation });
  }

  handleCheckBoxChange(checked) {
    const { location } = this.state;
    const newLocation = {
      ...location,
      isWarehouse: checked,
    };
    this.setState({ location: newLocation });
  }

  handleSave() {
    const { editing, onSave } = this.props;
    const { location } = this.state;
    const {
      name,
      type,
      brands,
      marketId,
      targetVehicleCounts,
      hubCapacity,
    } = location;
    const isStation = type === LocationTypeNames.STATION;
    const isHub = type === LocationTypeNames.HUB;
    const linkTarget = get(targetVehicleCounts, OpsBrandNamesLocationTargets.LINK);
    const aggregateTarget = getTargetVehicleCounts(targetVehicleCounts);
    const capacity = hubCapacity || 0;
    if (isHub && capacity < aggregateTarget) {
      return this.setState({ error: true, errorMessage: 'Per brand targets exceed hub capacity.' });
    }
    if (!name) {
      return this.setState({ error: true, errorMessage: 'Please enter a name.' });
    }
    if (!type) {
      return this.setState({ error: true, errorMessage: 'Please enter a type.' });
    }
    if (isEmpty(brands)) {
      return this.setState({ error: true, errorMessage: 'Please enter a brand.' });
    }
    if (!marketId) {
      return this.setState({ error: true, errorMessage: 'Please enter a Market.' });
    }

    if (isLink(location) && !linkTarget) {
      return this.setState({ error: true, errorMessage: 'Please enter a Link Target Vehicle Quantity.' });
    }

    if (!isStation) {
      location.dockingSpaces = undefined;
    }
    if (!isHub) {
      location.hubCapacity = undefined;
    }

    const coordinatesAsStrings = get(location, 'geo.coordinates');
    if (coordinatesAsStrings) {
      const coordinatesAsNumbers = [
        Number(coordinatesAsStrings[0]), Number(coordinatesAsStrings[1]),
      ];
      set(location, 'geo.coordinates', coordinatesAsNumbers);
    }
    onSave(location, editing);
  }

  selectedType(type) {
    const { location } = this.state;
    const locationType = location.type;
    return locationType === type ? 'contained' : 'outlined';
  }

  selectedStatus(status) {
    const { location } = this.state;
    const locationStatus = location.status;
    if (locationStatus === status) {
      return 'primary';
    }
  }

  disabled(brand) {
    // allows two ways to disable an input
    // 1) pass blocked brand as parameter
    // or 2) if the location.brand is blocked
    const { location } = this.state;
    const BLOCKED_BRANDS = ['Zagster', 'Pace'];

    if (includes(BLOCKED_BRANDS, brand)) {
      return true;
    }

    return includes(BLOCKED_BRANDS, location.brand);
  }

  render() {
    const { isOpen, onCancel } = this.props;
    const {
      location, errorMessage, unSavedChanges, error,
    } = this.state;

    const isHub = location.type === LocationTypeNames.HUB;
    const isStation = location.type === LocationTypeNames.STATION;
    const isBrandSelectDisabled = get(location, 'brands.length', 0) === 1
        && location.brands[0] === OpsBrandNames.ZAGSTER;

    return (
      <Dialog
        open={isOpen}
        fullWidth
        maxWidth="xl"
        aria-labelledby="form-dialog-title"
        onClose={this.closeModal}
      >
        <DialogTitle id="form-dialog-title">
          <Grid container justifyContent="space-between">
            <Grid item>
              Location:
              {` ${location.name}`}
            </Grid>
            <Grid item>
              <Button onClick={this.closeModal} color="secondary">
                Cancel
              </Button>
              <Button onClick={this.handleSave} color="primary">
                Save
              </Button>
            </Grid>
          </Grid>
        </DialogTitle>
        {unSavedChanges
          ? (
            <DialogActions style={styles.centerContent}>
              <Typography>You have unsaved changes.  Would you like to save?</Typography>
              <Button onClick={onCancel} color="secondary">
                No
              </Button>
              <Button onClick={this.handleSave} color="primary">
                Yes
              </Button>
            </DialogActions>
          ) : (
            <div>
              <DialogContent>
                <Grid container spacing={3}>
                  {error && (
                    <Grid item xs={12}>
                      <ErrorMessage errorMessage={errorMessage} />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={4}>
                    <div style={styles.group}>
                      {locationTypeNames.map((type) => (
                        <Button
                          key={`location_types_${type}`}
                          fullWidth
                          variant={this.selectedType(type)}
                          color="primary"
                          name="type"
                          value={type}
                          disabled={this.disabled()}
                          onClick={(event) => this.handleButtonEventChange(event)}
                        >
                          <div name="type" value={type}>{type}</div>
                        </Button>
                      ))}
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <MultiSelect
                      placeholder="Select Brand(s)"
                      options={opsBrandNameValues.map((brand) => ({
                        value: brand,
                        label: brand,
                        isDisabled: brand === OpsBrandNames.ZAGSTER,
                      }))}
                      value={location.brands || []}
                      handleChange={this.handleBrandChange}
                      isDisabled={isBrandSelectDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl>
                      <FormControlLabel
                        label="Warehouse"
                        labelPlacement="start"
                        classes={{ label: styles.checkbox }}
                        control={(
                          <Checkbox
                            checked={location.isWarehouse || false}
                            onChange={(e) => this.handleCheckBoxChange(e.target.checked)}
                            color="primary"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid container spacing={3}>
                    <Grid item xs={6} sx={{ mt: 2, pl: 4 }}>
                      <SmallMap
                        allowDrag
                        center={this.coordinates}
                        handlePositionChange={this.handleCenterChangeViaMap}
                      />
                    </Grid>
                    <Grid item xs={6} sx={{ mt: 2 }}>
                      <Grid container>
                        <Grid item xs={12}>
                          <InputLabel>
                            Name
                          </InputLabel>
                          <TextField
                            autoFocus
                            value={location.name || ''}
                            name="name"
                            margin="dense"
                            type="text"
                            variant="standard"
                            fullWidth
                            disabled={this.disabled()}
                            onChange={this.handleChange}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <InputLabel>
                            Status
                          </InputLabel>
                          <Select
                            style={styles.wranglerEditControl}
                            onChange={this.handleChange}
                            value={location.status || ''}
                            name="status"
                            variant="standard"
                            fullWidth
                          >
                            {[ACTIVE, INACTIVE].map((state) => (
                              <MenuItem dense key={state} value={state}>
                                <ListItemText primary={state} />
                              </MenuItem>
                            ))}
                          </Select>
                        </Grid>
                        <Grid item xs={9} />
                        <Grid item xs={6} sx={{ pr: 1 }}>
                          <InputLabel>
                            Center - Latitude
                          </InputLabel>
                          <TextField
                            margin="dense"
                            type="number"
                            variant="standard"
                            value={get(location, 'geo.coordinates[1]')}
                            fullWidth
                            disabled
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <InputLabel>
                            Center - Longitude
                          </InputLabel>
                          <TextField
                            margin="dense"
                            type="number"
                            variant="standard"
                            value={get(location, 'geo.coordinates[0]')}
                            fullWidth
                            disabled
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ mt: 2 }}>
                          <InputLabel>
                            Address
                          </InputLabel>
                          <TextField
                            value={location.address || ''}
                            name="address"
                            margin="dense"
                            type="text"
                            variant="standard"
                            fullWidth
                            disabled={this.disabled()}
                            onChange={this.handleChange}
                          />
                        </Grid>
                        <Grid item xs={6} sx={{ pr: 1, mt: 2 }}>
                          <InputLabel>
                            City
                          </InputLabel>
                          <TextField
                            value={location.city || ''}
                            name="city"
                            margin="dense"
                            type="text"
                            variant="standard"
                            fullWidth
                            disabled={this.disabled()}
                            onChange={this.handleChange}
                          />
                        </Grid>
                        <Grid item xs={3} sx={{ pr: 1, mt: 2 }}>
                          <InputLabel>
                            State
                          </InputLabel>
                          <Select
                            style={styles.wranglerEditControl}
                            onChange={this.handleChange}
                            value={location.state || ''}
                            name="state"
                            variant="standard"
                            fullWidth
                            disabled={this.disabled()}
                          >
                            {stateAbbreviations().map((state) => (
                              <MenuItem dense key={state} value={state}>
                                <ListItemText primary={state} />
                              </MenuItem>
                            ))}
                          </Select>
                        </Grid>
                        <Grid item xs={3} sx={{ mt: 2 }}>
                          <InputLabel>
                            Zip
                          </InputLabel>
                          <TextField
                            value={location.zip || ''}
                            name="zip"
                            margin="dense"
                            type="text"
                            variant="standard"
                            fullWidth
                            disabled={this.disabled()}
                            onChange={this.handleChange}
                          />
                        </Grid>
                        <Grid item xs={4} sx={{ pr: 1, mt: 2 }}>
                          <InputLabel>
                            Actual Vehicles
                          </InputLabel>
                          <TextField
                            value={get(location, 'vehicleIds', []).length || ''}
                            name="vehicleCount"
                            margin="dense"
                            type="number"
                            variant="standard"
                            fullWidth
                            disabled
                            onChange={this.handleChange}
                          />
                        </Grid>
                        {isStation && (
                          <Grid item xs={4} sx={{ pr: 1, mt: 2 }}>
                            <InputLabel>
                              Docking Spaces
                            </InputLabel>
                            <TextField
                              value={location.dockingSpaces || ''}
                              name="dockingSpaces"
                              margin="dense"
                              type="number"
                              variant="standard"
                              fullWidth
                              disabled={this.disabled()}
                              onChange={this.handleChange}
                              InputProps={{ inputProps: { min: 0 } }}
                              onInput={(e) => {
                                e.target.value = e.target.value.replace(/\D/g, '');
                              }}
                            />
                          </Grid>
                        )}
                        {isHub && (
                          <Grid item xs={4} sx={{ pr: 1, mt: 2 }}>
                            <InputLabel>
                              Hub Capacity
                            </InputLabel>
                            <TextField
                              value={location.hubCapacity || ''}
                              name="hubCapacity"
                              margin="dense"
                              type="number"
                              variant="standard"
                              fullWidth
                              disabled={this.disabled()}
                              onChange={this.handleChange}
                              InputProps={{ inputProps: { min: 0 } }}
                              onInput={(e) => {
                                e.target.value = e.target.value.replace(/\D/g, '');
                              }}
                            />
                          </Grid>
                        )}
                        {isLink(location) && (
                          <Grid item xs={4} sx={{ mt: 2 }}>
                            <InputLabel>
                              Target Link Vehicles
                            </InputLabel>
                            <TextField
                              value={get(location, 'targetVehicleCounts.link') || ''}
                              name={OpsBrandNamesLocationTargets.LINK}
                              margin="dense"
                              type="number"
                              variant="standard"
                              fullWidth
                              onChange={this.handleTargetVehicleChange}
                              InputProps={{ inputProps: { min: 0 } }}
                              onInput={(e) => {
                                e.target.value = e.target.value.replace(/\D/g, '');
                              }}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                    <Grid container sx={{ m: 2, ml: 5 }}>
                      <Grid item xs={12} sx={{ mt: 2 }}>
                        <InputLabel>
                          Description
                        </InputLabel>
                        <TextField
                          value={location.description || ''}
                          name="description"
                          margin="dense"
                          maxRows="5"
                          multiline
                          type="text"
                          variant="standard"
                          fullWidth
                          onChange={this.handleChange}
                        />
                        <Typography variant="h5">
                          Location Pictures
                          <Button
                            onClick={this.uploadImage}
                            variant="outlined"
                            color="secondary"
                            sx={{ m: 2 }}
                          >
                            UPLOAD
                          </Button>
                        </Typography>

                      </Grid>
                      {get(location, 'images', []).map((image) => (
                        <div key={image.url}>
                          <IconButton
                            onClick={() => this.deleteImage(image.url)}
                          >
                            <Delete style={{ color: 'red' }} />
                          </IconButton>
                          <Image
                            alt="Location Image"
                            cloudName={CLOUD_NAME}
                            publicId={image.url && `${LOCATION_BUCKET_NAME}/${convertImageUrlToPublicId(image.url)}`}
                            width="600"
                            height="400"
                            border="1px_solid_black"
                            crop="pad"
                          />
                        </div>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              </DialogContent>
            </div>
          )}
      </Dialog>
    );
  }
}

const mapStateToProps = ({ markets }) => ({
  markets: markets.markets,
});

export default connect(
  mapStateToProps,
  undefined,
)(withRouter(LocationEditForm));
